import { useState, useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ProfileSidebar } from "@/components/profile/ProfileSidebar";
import { TokenPlans } from "@/components/profile/TokenPlans";
import { GuideGrid } from "@/components/profile/GuideGrid";
import { ProfileContent } from "@/components/profile/ProfileContent";
import { Menu, Plus, X, Loader2, Coins } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@/lib/hooks";
import { user } from "@/api/apiService";
import { guides } from "@/api/apiService";
import { updateMapStyle } from "@/features/user/userSlice";
import { fetchBalanceSuccess } from "@/features/tokens/tokensSlice";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import MapStylePreview from "@/components/MapStylePreview";
import { saveSettings, loadSettings } from "@/lib/utils";

const Profile = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const [myGuidesSearch, setMyGuidesSearch] = useState("");
  const [savedGuidesSearch, setSavedGuidesSearch] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(state => state.user.isAuthenticated);
  const profile = useAppSelector(state => state.user.profile);
  const userGuides = useAppSelector(state => state.guides.lastGuides);
  const savedGuideIds = useAppSelector(state => state.guides.savedGuides);
  const { balance } = useAppSelector(state => state.tokens);
  const mapStyle = useAppSelector(state => state.user.preferences.mapStyle);

  useEffect(() => {
    const settings = loadSettings();
    if (settings.mapStyle) {
      dispatch(updateMapStyle(settings.mapStyle));
    }
  }, [dispatch]);

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const userData = await user();

        if (userData && userData.tokens !== undefined) {
          dispatch(fetchBalanceSuccess(userData.tokens));
        }

      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchUserData().then();
    } else {
      setLoading(false);
    }
  }, [dispatch, isAuthenticated]);

  const handleMapStyleChange = (value: 'default' | 'dark' | 'light' | 'theme_color') => {
    dispatch(updateMapStyle(value));
    saveSettings({ ...loadSettings(), mapStyle: value });
  };

  if (!isAuthenticated && !loading) {
    return <Navigate to="/login" />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
            <p className="mt-4 text-lg text-muted-foreground">Loading profile...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 container mx-auto px-2 md:px-4 py-4 md:py-8 mt-14 md:mt-16 relative">
        {/* Mobile Menu Button */}
        <Button
          variant="secondary"
          size="icon"
          className="md:hidden fixed top-[4.2rem] left-2 z-50 h-9 w-9 rounded-full shadow-md hover:shadow-lg transition-all duration-200 backdrop-blur-sm bg-background/95"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        >
          {isSidebarOpen ? (
            <X className="h-4 w-4" />
          ) : (
            <Menu className="h-4 w-4" />
          )}
        </Button>

        {/* Create Guide FAB */}
        <Link to="/create-guide">
          <Button
            className="md:hidden fixed bottom-4 right-4 rounded-full w-12 h-12 shadow-lg hover:shadow-xl z-50 transition-all duration-200 bg-primary hover:bg-primary/90"
            size="icon"
            aria-label="Create new guide"
          >
            <Plus className="h-5 w-5" />
          </Button>
        </Link>

        <div className="flex flex-col md:flex-row gap-3 md:gap-6">
          {/* Mobile Sidebar Overlay */}
          {isSidebarOpen && (
            <div
              className="md:hidden fixed inset-0 bg-background/80 backdrop-blur-sm z-40 transition-opacity duration-300"
              onClick={() => setIsSidebarOpen(false)}
              role="button"
              tabIndex={0}
              aria-label="Close menu"
            />
          )}

          {/* Sidebar */}
          <div
            className={`
              fixed md:relative md:block top-0 left-0 w-[80%] md:w-auto h-[100dvh] z-50
              transition-all duration-300 ease-in-out
              ${isSidebarOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0 md:opacity-100'}
              md:translate-x-0 md:h-auto
              drop-shadow-xl md:drop-shadow-none
            `}
          >
            <div className="h-full overflow-y-auto pt-16 md:pt-0 bg-background md:bg-transparent">
              <ProfileSidebar
                user={profile}
                activeTab={activeTab}
                onTabChange={(tab) => {
                  setActiveTab(tab);
                  setIsSidebarOpen(false);
                }}
              />
            </div>
          </div>

          {/* Main Content */}
          <Card className="flex-1 p-3 md:p-6 mb-14 md:mb-0">{/* Added bottom margin on mobile to avoid FAB overlap */}
            {activeTab === "profile" && (
              <div className="space-y-4 md:space-y-8">
                <div className="bg-primary/5 p-4 md:p-6 rounded-lg mb-4 md:mb-8">
                  <div className="flex items-center justify-between mb-4 md:mb-6">
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold">Your Tokens</h3>
                      <p className="text-sm md:text-base text-muted-foreground">Use tokens to generate AI city guides</p>
                    </div>
                    <div className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-primary/10 rounded-full">
                      <Coins className="h-4 w-4 text-primary"/>
                      <span className="text-sm font-medium">{balance} <span className="hidden md:inline">available tokens</span></span>
                    </div>
                  </div>
                  <TokenPlans />
                </div>
                <ProfileContent user={profile} />
              </div>
            )}

            {/*{activeTab === "guides" && (*/}
            {/*  <GuideGrid*/}
            {/*    guides={userGuides}*/}
            {/*    searchQuery={myGuidesSearch}*/}
            {/*    onSearchChange={setMyGuidesSearch}*/}
            {/*    title="My Created Guides"*/}
            {/*    searchPlaceholder="Search your guides..."*/}
            {/*  />*/}
            {/*)}*/}

            {/*{activeTab === "saved" && (*/}
            {/*  <GuideGrid*/}
            {/*    guides={userGuides.filter(guide => savedGuideIds.includes(guide.id))}*/}
            {/*    searchQuery={savedGuidesSearch}*/}
            {/*    onSearchChange={setSavedGuidesSearch}*/}
            {/*    title="Saved Guides"*/}
            {/*    searchPlaceholder="Search saved guides..."*/}
            {/*  />*/}
            {/*)}*/}

            {activeTab === "settings" && (
              <div className="space-y-4 md:space-y-6">
                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Account Settings</h3>

                <div className="bg-accent/5 p-4 rounded-lg">
                  <h4 className="text-base font-medium mb-2">Map Appearance</h4>
                  <p className="text-sm text-muted-foreground mb-4">Choose how you want the maps to appear throughout the application</p>

                  <RadioGroup
                      value={mapStyle}
                      onValueChange={(value) => handleMapStyleChange(value as 'default' | 'dark' | 'light')}
                      className="grid grid-cols-1 md:grid-cols-3 gap-4"
                  >
                    <div className="border rounded-md p-4 cursor-pointer hover:bg-accent/5"
                         onClick={() => handleMapStyleChange('theme_color')}>
                      <div className="flex items-start space-x-2 mb-3">
                        <RadioGroupItem value="theme_color" id="theme_color-style"/>
                        <div className="grid gap-1.5">
                          <Label htmlFor="theme_color-style" className="font-medium cursor-pointer">Theme color</Label>
                          <p className="text-sm text-muted-foreground">Neutral tones, ideal for regular use</p>
                        </div>
                      </div>
                      <MapStylePreview styleName="theme_color" height="120px"/>
                    </div>

                    <div className="border rounded-md p-4 cursor-pointer hover:bg-accent/5"
                         onClick={() => handleMapStyleChange('default')}>
                      <div className="flex items-start space-x-2 mb-3">
                        <RadioGroupItem value="default" id="default-style"/>
                        <div className="grid gap-1.5">
                          <Label htmlFor="default-style" className="font-medium cursor-pointer">Default</Label>
                          <p className="text-sm text-muted-foreground">Neutral tones, ideal for regular use</p>
                        </div>
                      </div>
                      <MapStylePreview styleName="default" height="120px"/>
                    </div>

                    <div className="border rounded-md p-4 cursor-pointer hover:bg-accent/5"
                         onClick={() => handleMapStyleChange('dark')}>
                      <div className="flex items-start space-x-2 mb-3">
                        <RadioGroupItem value="dark" id="dark-style"/>
                        <div className="grid gap-1.5">
                          <Label htmlFor="dark-style" className="font-medium cursor-pointer">Dark</Label>
                          <p className="text-sm text-muted-foreground">Darker theme, easier on the eyes</p>
                        </div>
                      </div>
                      <MapStylePreview styleName="dark" height="120px"/>
                    </div>

                    <div className="border rounded-md p-4 cursor-pointer hover:bg-accent/5"
                         onClick={() => handleMapStyleChange('light')}>
                      <div className="flex items-start space-x-2 mb-3">
                        <RadioGroupItem value="light" id="light-style"/>
                        <div className="grid gap-1.5">
                          <Label htmlFor="light-style" className="font-medium cursor-pointer">Light</Label>
                          <p className="text-sm text-muted-foreground">Brighter look with vibrant colors</p>
                        </div>
                      </div>
                      <MapStylePreview styleName="light" height="120px"/>
                    </div>
                  </RadioGroup>
                </div>

                {/* Add more settings options here */}
              </div>
            )}
          </Card>
        </div>
      </main>
      <Footer/>
    </div>
  );
};

export default Profile;
